import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRightWhiteIcon,
  BackArrowSmallIcon,
  LogoWithTitlePNG,
} from '../../../assets';
import { useAuth } from '../../../context/AuthContext';

const PrivacyPolicy: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="privacy-policy">
      <div className="privacy-policy__container">
        {!currentUser?.id && (
          <div className="privacy-policy__button-container">
            <button
              className="privacy-policy__back-button"
              onClick={() => navigate(-1)}
            >
              <div className="privacy-policy__back-button-icon">
                <ArrowRightWhiteIcon />
              </div>
              <span>Back</span>
            </button>
          </div>
        )}
        <div className="privacy-policy__image-container">
          <img src={LogoWithTitlePNG} alt="" />
        </div>
        <h1 className="privacy-policy__title">Privacy Policy</h1>
        <p className="privacy-policy__description">
          This Privacy Policy describes Our policies and procedures on the
          collection, use, and disclosure of Your information when You use the
          Service and informs You about Your privacy rights and how the law
          protects You. We use Your Personal Data to provide and improve the
          Service. By using the Service, You agree to the collection and use of
          information in accordance with this Privacy Policy.
        </p>

        <p className="privacy-policy__description">
          The words with capitalized initials have meanings defined under the
          following conditions. These definitions shall have the same meaning
          regardless of whether they appear in singular or in plural.
        </p>
        <ul className="privacy-policy__numbered-list">
          <li className="privacy-policy__numbered-list-item">
            <h2 className="privacy-policy__subtitle">Definitions</h2>
            <p className="privacy-policy__paragraph">
              For the purposes of this Privacy Policy:
            </p>
            <ul className="privacy-policy__list">
              <li className="privacy-policy__list-item">
                <strong>"Account"</strong> means a unique account created for
                You to access our Service or parts of our Service.
              </li>
              <li className="privacy-policy__list-item">
                <strong>"Business",</strong> for the purpose of the CCPA
                (California Consumer Privacy Act), means the Company as the
                legal entity that collects Consumers' personal information and
                determines the purposes and means of the processing of
                Consumers' personal information, or on behalf of which such
                information is collected and that alone, or jointly with others,
                determines the purposes and means of the processing of
                consumers' personal information, that does business in the State
                of California.
              </li>
              <li className="privacy-policy__list-item">
                <strong>"Company"</strong> (referred to herein as either the
                "Company", "We", "Us" or "Our") means Astreaux, a California
                LLC. For the purpose of the GDPR, the Company is the Data
                Controller.
              </li>
              <li className="privacy-policy__list-item">
                <strong>“Consumer”, </strong>for the purpose of the CCPA
                (California Consumer Privacy Act), means a natural person who is
                a California resident. A resident, as defined in the law,
                includes (1) every individual who is in the USA for other than a
                temporary or transitory purpose, and (2) every individual who is
                domiciled in the USA who is outside the USA for a temporary or
                transitory purpose.
              </li>
              <li className="privacy-policy__list-item">
                <strong>"Cookies"</strong> means small files that are placed on
                Your computer, mobile device, or any other device by a website,
                containing the details of Your browsing history on that website
                among its many uses.
              </li>
              <li className="privacy-policy__list-item">
                <strong>“Data Controller”,</strong> for the purposes of the GDPR
                (General Data Protection Regulation), means the Company as the
                legal person which alone or jointly with others determines the
                purposes and means of the processing of Personal Data.
              </li>
              <li className="privacy-policy__list-item">
                <strong>"Device"</strong> means any electronic device that can
                access the Service such as a computer, a cellular phone, or a
                digital tablet.
              </li>
              <li className="privacy-policy__list-item">
                <strong>“Do Not Track (DNT)”</strong> means a concept that has
                been promoted by US regulatory authorities, in particular the
                U.S. Federal Trade Commission (FTC), for the Internet industry
                to develop and implement a mechanism for allowing internet users
                to control the tracking of their online activities across
                websites.
              </li>
              <li className="privacy-policy__list-item">
                <strong>“Personal Data”</strong> means any information that
                relates to an identified or identifiable individual. For the
                purposes of GDPR, Personal Data means any information relating
                to You such as a name, an identification number, location data,
                online identifier, or to one or more factors specific to the
                physical, physiological, genetic, mental, economic, cultural, or
                social identity. For the purposes of the CCPA, Personal Data
                means any information that identifies, relates to, describes, or
                is capable of being associated with, or could reasonably be
                linked, directly or indirectly, with You.
              </li>
              <li className="privacy-policy__list-item">
                <strong>“Sale”,</strong> for the purpose of the CCPA (California
                Consumer Privacy Act), means selling, renting, releasing,
                disclosing, disseminating, making available, transferring, or
                otherwise communicating orally, in writing, or by electronic or
                other means, a Consumer's personal information to another
                business or a third party for monetary or other valuable
                consideration.
              </li>
              <li className="privacy-policy__list-item">
                <strong>“Service”</strong> means the online and/or mobile
                services, website, and software provided on or in connection
                with the service provided by Astreaux under this Agreement and
                as detailed on Astreaux’s website at{' '}
                <Link
                  to="https://www.astreaux.ai"
                  target="_blank"
                  className="primary-link"
                >
                  www.Astreaux.ai
                </Link>
                .
              </li>
              <li className="privacy-policy__list-item">
                <strong>“Service Provider”</strong> means any natural or legal
                person who processes the data on behalf of the Company. It
                refers to third-party companies or individuals employed by the
                Company to facilitate the Service, to provide the Service on
                behalf of the Company, to perform services related to the
                Service, or to assist the Company in analyzing how the Service
                is used. For the purpose of the GDPR, Service Providers are
                considered Data Processors.
              </li>
              <li className="privacy-policy__list-item">
                <strong>“Third-party Social Media Service”</strong> means any
                website or any social network website through which a User can
                log in or create an account to use the Service.
              </li>
              <li className="privacy-policy__list-item">
                <strong>“Usage Data”</strong> means data collected
                automatically, either generated by the use of the Service or
                from the Service infrastructure itself (for example, the
                duration of a page visit).
              </li>
              <li className="privacy-policy__list-item">
                <strong>“Website”</strong> means{' '}
                <Link
                  to="https://www.astreaux.ai"
                  target="_blank"
                  className="primary-link"
                >
                  https://www.Astreaux.ai
                </Link>
              </li>
              <li className="privacy-policy__list-item">
                <strong>“You”</strong> means the individual accessing or using
                the Service, or the company, or other legal entity on behalf of
                which such individual is accessing or using the Service, as
                applicable. Under GDPR (General Data Protection Regulation), You
                can be referred to as the Data Subject or as the User as you are
                the individual using the Service.
              </li>
            </ul>
          </li>
          <li className="privacy-policy__numbered-list-item">
            <h2 className="privacy-policy__subtitle">
              Collecting and Using Your Personal Data
            </h2>
            <ul className="privacy-policy__list">
              <li className="privacy-policy__list-item">
                <h4>Personal Data </h4>
                <p>
                  While using Our Service, We may ask You to provide Us with
                  certain personally identifiable information that can be used
                  to contact or identify You. Personally identifiable
                  information may include, but is not limited to:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__list-dashed-item">
                    Email address
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    First name and last name
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Phone number
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Address, State, Province, ZIP/Postal code, City
                  </li>
                </ul>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Usage Data</h4>
                <p className="privacy-policy__list-item">
                  Usage Data is collected automatically when using the Service.
                  Usage Data may include information such as Your Device's
                  Internet Protocol address (e.g., IP address), browser type,
                  browser version, the pages of our Service that You visit, the
                  time and date of Your visit, the time spent on those pages,
                  unique device identifiers, and other diagnostic data.
                </p>
                <p className="privacy-policy__list-item">
                  When You access the Service by or through a mobile device, We
                  may collect certain information automatically, including, but
                  not limited to, the type of mobile device You use, Your mobile
                  device unique ID, the IP address of Your mobile device, Your
                  mobile operating system, the type of mobile Internet browser
                  You use, unique device identifiers, and other diagnostic data.
                </p>
                <p className="privacy-policy__list-item">
                  We may also collect information that Your browser sends
                  whenever You visit our Service or when You access the Service
                  by or through a mobile device.
                </p>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Information from Third-Party Social Media Services</h4>
                <p>
                  The Company allows You to create an account and log in to use
                  the Service through the following Third-party Social Media
                  Services:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__list-dashed-item">Google</li>
                  <li className="privacy-policy__list-dashed-item">Facebook</li>
                  <li className="privacy-policy__list-dashed-item">
                    Instagram
                  </li>
                </ul>
                <p className="privacy-policy__list-item">
                  If You decide to register through or otherwise grant us access
                  to a Third-Party Social Media Service, We may collect Personal
                  Data that is already associated with Your Third-Party Social
                  Media Service's account, such as Your name, Your email
                  address, Your activities, or Your contact list associated with
                  that account.
                </p>
                <p className="privacy-policy__list-item">
                  You may also have the option of sharing additional information
                  with the Company through Your Third-Party Social Media
                  Service's account. If You choose to provide such information
                  and Personal Data, during registration or otherwise, You are
                  giving the Company permission to use, share, and store it in a
                  manner consistent with this Privacy Policy.
                </p>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Tracking Technologies and Cookies</h4>
                <p className="privacy-policy__list-item">
                  We use Cookies and similar tracking technologies to track the
                  activity on Our Service and store certain information.
                  Tracking technologies used are beacons, tags, and scripts to
                  collect and track information and to improve and analyze Our
                  Service. The technologies We use may include:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__list-dashed-item">
                    Cookies or Browser Cookies. A cookie is a small file placed
                    on Your Device. You can instruct Your browser to refuse all
                    Cookies or to indicate when a Cookie is being sent. However,
                    if You do not accept Cookies, You may not be able to use
                    some parts of our Service. Unless you have adjusted Your
                    browser setting so that it will refuse Cookies, our Service
                    may use Cookies.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Flash Cookies. Certain features of our Service may use local
                    stored objects (or Flash Cookies) to collect and store
                    information about Your preferences or Your activity on our
                    Service. Flash Cookies are not managed by the same browser
                    settings as those used for Browser Cookies.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Web Beacons. Certain sections of our Service and our emails
                    may contain small electronic files known as web beacons
                    (also referred to as clear gifs, pixel tags, and
                    single-pixel gifs) that permit the Company, for example, to
                    count users who have visited those pages or opened an email
                    and for other related website statistics (for example,
                    recording the popularity of a certain section and verifying
                    system and server integrity).
                  </li>
                </ul>
                <p className="privacy-policy__list-item">
                  Cookies can be "Persistent" or "Session" Cookies. Persistent
                  Cookies remain on Your personal computer or mobile device when
                  You go offline, while Session Cookies are deleted as soon as
                  You close Your web browser. Learn more about cookies: What Are
                  Cookies?.
                </p>
                <p className="privacy-policy__list-item">
                  We use both Session and Persistent Cookies for the purposes
                  set out below:
                </p>
                <ul className="privacy-policy__roman-list">
                  <li className="privacy-policy__list-roman-item">
                    <h4>Necessary / Essential Cookies</h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Type:</strong> Session Cookies
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Administered by:</strong> Us
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Purpose:</strong> These Cookies are essential to
                        provide You with services available through the Website
                        and to enable You to use some of its features. They help
                        to authenticate users and prevent fraudulent use of user
                        accounts. Without these Cookies, the services that You
                        have asked for cannot be provided, and We only use these
                        Cookies to provide You with those services.
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>Cookies Policy / Notice Acceptance Cookies</h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Type:</strong> Persistent Cookies
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Administered by:</strong> Us
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Purpose:</strong> These Cookies identify if
                        users have accepted the use of cookies on the Website.
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>Functionality Cookies</h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Type:</strong> Persistent Cookies
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Administered by:</strong> Us
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Purpose:</strong> These Cookies allow us to
                        remember choices You make when You use the Website, such
                        as remembering your login details or language
                        preference. The purpose of these Cookies is to provide
                        You with a more personal experience and to avoid You
                        having to reenter your preferences every time You use
                        the Website.
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>Tracking and Performance Cookies</h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Type:</strong> Persistent Cookies
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Administered by:</strong> Third-Parties
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Purpose:</strong> These Cookies are used to
                        track information about traffic to the Website and how
                        users use the Website. The information gathered via
                        these Cookies may directly or indirectly identify you as
                        an individual visitor. This is because the information
                        collected is typically linked to a pseudonymous
                        identifier associated with the device you use to access
                        the Website. We may also use these Cookies to test new
                        pages, features or new functionality of the Website to
                        see how our users react to them.
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>Targeting and Advertising Cookies</h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Type:</strong> Persistent Cookies
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Administered by:</strong> Third-Parties
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Purpose:</strong> These Cookies track your
                        browsing habits to enable Us to show advertising which
                        is more likely to be of interest to You. These Cookies
                        use information about your browsing history to group You
                        with other users who have similar interests. Based on
                        that information, and with Our permission, third party
                        advertisers can place Cookies to enable them to show
                        adverts which We think will be relevant to your
                        interests while You are on third party websites.
                      </li>
                    </ul>
                  </li>
                </ul>
                <p className="privacy-policy__list-item">
                  For more information about the cookies we use and your choices
                  regarding cookies, please visit our Cookies Policy or the
                  Cookies section of our Privacy Policy.
                </p>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Use of Your Personal Data</h4>
                <p>
                  The Company may use Personal Data for the following purposes:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__list-dashed-item">
                    To provide and maintain our Service, including to monitor
                    the usage of our Service.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    To manage Your Account: to manage Your registration as a
                    user of the Service. The Personal Data You provide can give
                    You access to different functionalities of the Service that
                    are available to You as a registered user.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    For the performance of a contract: the development,
                    compliance, and undertaking of the purchase contract for the
                    products, items, or services You have purchased or of any
                    other contract with Us through the Service.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    To contact You: To contact You by email, telephone calls,
                    SMS, or other equivalent forms of electronic communication,
                    such as a mobile application's push notifications regarding
                    updates or informative communications related to the
                    functionalities, products, or contracted services, including
                    the security updates, when necessary or reasonable for their
                    implementation.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    To provide You with news, special offers, and general
                    information about other goods, services, and events which we
                    offer that are similar to those that you have already
                    purchased or enquired about unless You have opted not to
                    receive such information.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    To manage Your requests: To attend and manage Your requests
                    to Us.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    To deliver targeted advertising to You: We may use Your
                    information to develop and display content and advertising
                    (and work with third-party vendors who do so) tailored to
                    Your interests and/or location and to measure its
                    effectiveness.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    For business transfers: We may use Your information to
                    evaluate or conduct a merger, divestiture, restructuring,
                    reorganization, dissolution, or other sale or transfer of
                    some or all of Our assets, whether as a going concern or as
                    part of bankruptcy, liquidation, or similar proceeding, in
                    which Personal Data held by Us about our Service users is
                    among the assets transferred.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    For other purposes: We may use Your information for other
                    purposes, such as data analysis, identifying usage trends,
                    determining the effectiveness of our promotional campaigns,
                    and to evaluate and improve our Service, products, services,
                    marketing, and your experience.
                  </li>
                </ul>
                <p className="privacy-policy__list-item">
                  We may share Your personal information in the following
                  situations:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__list-dashed-item">
                    With Service Providers: We may share Your personal
                    information with Service Providers to monitor and analyze
                    the use of our Service, to show advertisements to You to
                    help support and maintain Our Service, to advertise on
                    third-party websites to You after You visited our Service,
                    for payment processing, to contact You.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    For business transfers: We may share or transfer Your
                    personal information in connection with, or during
                    negotiations of, any merger, sale of Company assets,
                    financing, or acquisition of all or a portion of Our
                    business to another company.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    With Affiliates: We may share Your information with Our
                    affiliates, in which case we will require those affiliates
                    to honor this Privacy Policy. Affiliates include Our parent
                    company and any other subsidiaries, joint venture partners,
                    or other companies that We control or that are under common
                    control with Us.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    With business partners: We may share Your information with
                    Our business partners to offer You certain products,
                    services, or promotions.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    With other users: when You share personal information or
                    otherwise interact in the public areas with other users,
                    such information may be viewed by all users and may be
                    publicly distributed outside. If You interact with other
                    users or register through a Third-Party Social Media
                    Service, Your contacts on the Third-Party Social Media
                    Service may see Your name, profile, pictures, and
                    description of Your activity. Similarly, other users will be
                    able to view descriptions of Your activity, communicate with
                    You, and view Your profile.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    With Your consent: We may disclose Your personal information
                    for any other purpose with Your consent.
                  </li>
                </ul>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Retention of Your Personal Data</h4>
                <p className="privacy-policy__list-item">
                  The Company will retain Your Personal Data only for as long as
                  is necessary for the purposes set out in this Privacy Policy.
                  We will retain and use Your Personal Data to the extent
                  necessary to comply with our legal obligations (for example,
                  if we are required to retain your data to comply with
                  applicable laws), resolve disputes, and enforce our legal
                  agreements and policies.
                </p>
                <p className="privacy-policy__list-item">
                  The Company will also retain Usage Data for internal analysis
                  purposes. Usage Data is generally retained for a shorter
                  period of time, except when this data is used to strengthen
                  the security or to improve the functionality of Our Service,
                  or We are legally obligated to retain this data for longer
                  time periods.
                </p>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Transfer of Your Personal Data</h4>
                <p className="privacy-policy__list-item">
                  Your information, including Personal Data, is processed at the
                  Company's operating offices and in any other places where the
                  parties involved in the processing are located. It means that
                  this information may be transferred to — and maintained on —
                  computers located outside of Your state, province, country, or
                  other governmental jurisdiction where the data protection laws
                  may differ from those from Your jurisdiction.
                </p>
                <p className="privacy-policy__list-item">
                  Your consent to this Privacy Policy followed by Your
                  submission of such information represents Your agreement to
                  that transfer.
                </p>
                <p className="privacy-policy__list-item">
                  The Company will take all steps reasonably necessary to ensure
                  that Your data is treated securely and in accordance with this
                  Privacy Policy and no transfer of Your Personal Data will take
                  place to an organization or a country unless there are
                  adequate controls in place including the security of Your data
                  and other personal information.
                </p>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Disclosure of Your Personal Data</h4>
                <ul className="privacy-policy__roman-list">
                  <li className="privacy-policy__list-roman-item">
                    <h4>Business Transactions</h4>
                    <p className="privacy-policy__list-roman-item">
                      If the Company is involved in a merger, acquisition, or
                      asset sale, Your Personal Data may be transferred. We will
                      provide notice before Your Personal Data is transferred
                      and becomes subject to a different Privacy Policy.
                    </p>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>Law Enforcement</h4>
                    <p className="privacy-policy__list-roman-item">
                      Under certain circumstances, the Company may be required
                      to disclose Your Personal Data if required to do so by law
                      or in response to valid requests by public authorities
                      (e.g., a court or a government agency).
                    </p>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>Other Legal Requirements</h4>
                    <p className="privacy-policy__list-roman-item">
                      The Company may disclose Your Personal Data in the good
                      faith belief that such action is necessary to: Comply with
                      a legal obligation Protect and defend the rights or
                      property of the Company Prevent or investigate possible
                      wrongdoing in connection with the Service Protect the
                      personal safety of Users of the Service or the public
                      Protect against legal liability
                    </p>
                  </li>
                </ul>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Security of Your Personal Data</h4>
                <p className="privacy-policy__list-item">
                  The security of Your Personal Data is important to Us, but
                  remember that no method of transmission over the Internet, or
                  method of electronic storage is 100% secure. While We strive
                  to use commercially acceptable means to protect Your Personal
                  Data, We cannot guarantee its absolute security.
                </p>
              </li>
            </ul>
          </li>
          <li className="privacy-policy__numbered-list-item">
            <h2 className="privacy-policy__subtitle">
              Detailed Information on the Processing of Your Personal Data
            </h2>
            <p className="privacy-policy__paragraph">
              The Service Providers We use may have access to Your Personal
              Data. These third-party vendors collect, store, use, process, and
              transfer information about Your activity on Our Service in
              accordance with their Privacy Policies.
            </p>
            <ul className="privacy-policy__list">
              <li className="privacy-policy__list-item">
                <h4>Analytics</h4>
                <p>
                  We may use third-party Service providers to monitor and
                  analyze the use of our Service.
                </p>
                <ul className="privacy-policy__roman-list">
                  <li className="privacy-policy__list-roman-item">
                    <h4>Google Analytics</h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        Google Analytics is a web analytics service offered by
                        Google that tracks and reports website traffic. Google
                        uses the data collected to track and monitor the use of
                        our Service. This data is shared with other Google
                        services. Google may use the collected data to
                        contextualize and personalize the ads of its own
                        advertising network.
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        You can opt-out of having made your activity on the
                        Service available to Google Analytics by installing the
                        Google Analytics opt-out browser add-on. The add-on
                        prevents the Google Analytics JavaScript (ga.js,
                        analytics.js, and dc.js) from sharing information with
                        Google Analytics about visits activity.
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        For more information on the privacy practices of Google,
                        please visit the Google Privacy & Terms web page:{' '}
                        <Link
                          className="primary-link"
                          to="https://policies.google.com/privacy"
                          target="_blank"
                        >
                          Google Privacy
                        </Link>
                        .
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>Meta Business Suite</h4>
                    <p className="privacy-policy__list-item">
                      Meta Business Suite is a web analytics and business
                      management service offered by Meta (formerly Facebook)
                      that tracks and reports website traffic, user
                      interactions, and business performance. Meta uses the data
                      collected to monitor and analyze the use of our Service.
                      This data is shared with other Meta services. Meta may use
                      the collected data to contextualize and personalize the
                      ads within its own advertising network.
                    </p>
                    <p className="privacy-policy__list-item">
                      You can opt out of having your activity on the Service
                      available to Meta Business Suite by adjusting your ad
                      settings in your Meta account. This can be done by
                      accessing the Ad Settings page and selecting the
                      appropriate preferences.
                    </p>
                    <p className="privacy-policy__list-item">
                      More Information: For more information on the privacy
                      practices of Meta, please visit the Meta Privacy Policy
                      web page:{' '}
                      <Link
                        className="primary-link"
                        to="https://www.facebook.com/policy.php"
                        target="_blank"
                      >
                        Meta Privacy
                      </Link>
                      .
                    </p>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>Twilio</h4>
                    <p className="privacy-policy__list-item">
                      Twilio is a web analytics service offered by Twilio that
                      tracks and reports website traffic and user interactions.
                      Twilio uses the data collected to monitor and analyze the
                      use of our Service. This data is shared with other Twilio
                      services. Twilio may use the collected data to
                      contextualize and personalize the ads within its own
                      advertising network. You can opt out of having your
                      activity on the Service available to Twilio by installing
                      the appropriate browser add-on or adjusting your privacy
                      settings within the Twilio platform. This prevents the
                      Twilio JavaScript from sharing information with Twilio
                      about visit activities.
                    </p>
                    <p className="privacy-policy__list-item">
                      More Information: For more information on the privacy
                      practices of Twilio, please visit the Twilio Privacy
                      Policy web page:{' '}
                      <Link
                        className="primary-link"
                        to="https://www.twilio.com/privacy"
                        target="_blank"
                      >
                        Twilio Privacy
                      </Link>
                      .
                    </p>
                  </li>
                </ul>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Advertising</h4>
                <p className="privacy-policy__list-item">
                  We may use Service Providers to show advertisements to You to
                  help support and maintain Our Service.
                </p>
                <ul className="privacy-policy__roman-list">
                  <li className="privacy-policy__list-roman-item">
                    <h4>Google AdSense & DoubleClick Cookie</h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        Google, as a third-party vendor, uses cookies to serve
                        ads on our Service. Google's use of the DoubleClick
                        cookie enables it and its partners to serve ads to our
                        users based on their visit to our Service or other
                        websites on the Internet.
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        You may opt out of the use of the DoubleClick Cookie for
                        interest-based advertising by visiting the Google Ads
                        Settings web page:{' '}
                        <Link
                          className="primary-link"
                          to="http://www.google.com/ads/preferences/"
                          target="_blank"
                        >
                          Google Ads Settings
                        </Link>
                        .
                      </li>
                    </ul>
                    <li className="privacy-policy__list-roman-item">
                      <h4>Facebook</h4>
                      <ul className="privacy-policy__dashed-list">
                        <li className="privacy-policy__list-dashed-item">
                          Their Privacy Policy can be viewed at:{' '}
                          <Link
                            className="primary-link"
                            to="https://www.facebook.com/policy.php"
                            target="_blank"
                          >
                            Facebook Privacy
                          </Link>
                          .
                        </li>
                      </ul>
                    </li>
                    <li className="privacy-policy__list-roman-item">
                      <h4>Linkedin</h4>
                      <ul className="privacy-policy__dashed-list">
                        <li className="privacy-policy__list-dashed-item">
                          Their Privacy Policy can be viewed at:{' '}
                          <Link
                            className="primary-link"
                            to="https://www.linkedin.com/legal/ads-policy"
                            target="_blank"
                          >
                            Linkedin Privacy
                          </Link>
                          .
                        </li>
                      </ul>
                    </li>
                  </li>
                </ul>
                <li className="privacy-policy__list-item">
                  <h4>Email Marketing</h4>
                  <p className="privacy-policy__list-item">
                    We may use Your Personal Data to contact You with
                    newsletters, marketing or promotional materials, and other
                    information that may be of interest to You. You may opt-out
                    of receiving any, or all, of these communications from Us by
                    following the unsubscribe link or instructions provided in
                    any email We send or by contacting Us.
                  </p>
                  <p className="privacy-policy__list-item">
                    We may use Email Marketing Service Providers to manage and
                    send emails to You.
                  </p>
                </li>
                <li className="privacy-policy__list-item">
                  <h4>Payments</h4>
                  <p className="privacy-policy__list-item">
                    We may provide paid products and/or services within the
                    Service. In that case, we may use third-party services for
                    payment processing (e.g., payment processors).
                  </p>
                  <p className="privacy-policy__list-item">
                    We will not store or collect Your payment card details. That
                    information is provided directly to Our third-party payment
                    processors whose use of Your personal information is
                    governed by their Privacy Policy. These payment processors
                    adhere to the standards set by PCI-DSS as managed by the PCI
                    Security Standards Council, which is a joint effort of
                    brands like Visa, Mastercard, American Express, and
                    Discover. PCI-DSS requirements help ensure the secure
                    handling of payment information.
                  </p>
                </li>
                <li className="privacy-policy__list-item">
                  <h4>Payments</h4>
                  <p className="privacy-policy__list-item">
                    We may provide paid products and/or services within the
                    Service. In that case, we may use third-party services for
                    payment processing (e.g., payment processors).
                  </p>
                  <p className="privacy-policy__list-item">
                    We will not store or collect Your payment card details. That
                    information is provided directly to Our third-party payment
                    processors whose use of Your personal information is
                    governed by their Privacy Policy. These payment processors
                    adhere to the standards set by PCI-DSS as managed by the PCI
                    Security Standards Council, which is a joint effort of
                    brands like Visa, Mastercard, American Express, and
                    Discover. PCI-DSS requirements help ensure the secure
                    handling of payment information. Stripe Privacy Policy can
                    be viewed at:{' '}
                    <Link
                      className="primary-link"
                      to="https://stripe.com/us/privacy"
                      target="_blank"
                    >
                      Stripe Privacy
                    </Link>
                    .
                  </p>
                </li>
                <li className="privacy-policy__list-item">
                  <h4>Behavioral Remarketing</h4>
                  <p className="privacy-policy__list-item">
                    The Company uses remarketing services to advertise to You
                    after You accessed or visited our Service. We and Our
                    third-party vendors use cookies and non-cookie technologies
                    to help Us recognize Your Device and understand how You use
                    our Service so that We can improve our Service to reflect
                    Your interests and serve You advertisements that are likely
                    to be of more interest to You.
                  </p>
                  <p className="privacy-policy__list-item">
                    These third-party vendors collect, store, use, process, and
                    transfer information about Your activity on Our Service in
                    accordance with their Privacy Policies and to enable Us to:
                  </p>
                  <ul className="privacy-policy__dashed-list">
                    <li className="privacy-policy__list-dashed-item">
                      Measure and analyze traffic and browsing activity on Our
                      Service
                    </li>
                    <li className="privacy-policy__list-dashed-item">
                      Show advertisements for our products and/or services to
                      You on third-party websites or apps
                    </li>
                    <li className="privacy-policy__list-dashed-item">
                      Show advertisements for our products and/or services to
                      You on Measure and analyze the performance of Our
                      advertising campaigns
                    </li>
                  </ul>
                  <p className="privacy-policy__list-item">
                    Some of these third-party vendors may use non-cookie
                    technologies that may not be impacted by browser settings
                    that block cookies. Your browser may not permit You to block
                    such technologies. You can use the following third-party
                    tools to decline the collection and use of information for
                    the purpose of serving You interest-based advertising:
                  </p>
                  <ul className="privacy-policy__dashed-list">
                    <li className="privacy-policy__list-dashed-item">
                      The NAI's opt-out platform:{' '}
                      <Link
                        className="primary-link"
                        to="http://www.networkadvertising.org/choices/"
                        target="_blank"
                      >
                        NAI Opt-out
                      </Link>
                      .
                    </li>
                    <li className="privacy-policy__list-dashed-item">
                      The EDAA's opt-out platform:{' '}
                      <Link
                        className="primary-link"
                        to="http://www.youronlinechoices.com/"
                        target="_blank"
                      >
                        Your Online Choices
                      </Link>
                      .
                    </li>
                    <li className="privacy-policy__list-dashed-item">
                      The DAA's opt-out platform:{' '}
                      <Link
                        className="primary-link"
                        to="http://optout.aboutads.info/?c=2&lang=EN"
                        target="_blank"
                      >
                        DAA Opt-out
                      </Link>
                      .
                    </li>
                  </ul>
                  <p className="privacy-policy__list-item">
                    You may opt-out of all personalized advertising by enabling
                    privacy features on Your mobile device such as Limit Ad
                    Tracking (iOS) and Opt Out of Ads Personalization (Android).
                    See Your mobile device Help system for more information.
                  </p>
                  <p className="privacy-policy__list-item">
                    We may share information, such as hashed email addresses (if
                    available) or other online identifiers collected on Our
                    Service with these third-party vendors. This allows Our
                    third-party vendors to recognize and deliver You ads across
                    devices and browsers. To read more about the technologies
                    used by these third-party vendors and their cross-device
                    capabilities please refer to the Privacy Policy of each
                    vendor listed below.
                  </p>
                  <p className="privacy-policy__list-item">
                    The third-party vendors we use are:
                  </p>
                  <ul className="privacy-policy__roman-list">
                    <li className="privacy-policy__list-roman-item">
                      <h4>Google Ads (AdWords)</h4>
                      <ul className="privacy-policy__dashed-list">
                        <li className="privacy-policy__list-dashed-item">
                          Google Ads (AdWords) remarketing service is provided
                          by Google Inc.
                        </li>
                        <li className="privacy-policy__list-dashed-item">
                          You can opt-out of Google Analytics for Display
                          Advertising and customize the Google Display Network
                          ads by visiting the{' '}
                          <Link
                            className="primary-link"
                            to="http://www.google.com/settings/ads"
                            target="_blank"
                          >
                            Google Ads Settings
                          </Link>{' '}
                          page.
                        </li>
                        <li className="privacy-policy__list-dashed-item">
                          Google also recommends installing the{' '}
                          <Link
                            className="primary-link"
                            to="https://tools.google.com/dlpage/gaoptout"
                            target="_blank"
                          >
                            Google Analytics Opt-out Browser Add-on
                          </Link>{' '}
                          for your web browser. Google Analytics Opt-out Browser
                          Add-on provides visitors with the ability to prevent
                          their data from being collected and used by Google
                          Analytics. page.
                        </li>
                        <li className="privacy-policy__list-dashed-item">
                          For more information on the privacy practices of
                          Google, please visit the Google Privacy & Terms web
                          page:{' '}
                          <Link
                            className="primary-link"
                            to="https://policies.google.com/technologies/ads"
                            target="_blank"
                          >
                            Google Privacy
                          </Link>
                          .
                        </li>
                      </ul>
                    </li>
                    <li className="privacy-policy__list-roman-item">
                      <h4>Meta</h4>
                      <ul className="privacy-policy__dashed-list">
                        <li className="privacy-policy__list-dashed-item">
                          Meta's remarketing service is provided by Meta
                          Platforms, Inc.
                        </li>
                        <li className="privacy-policy__list-dashed-item">
                          You can learn more about interest-based advertising
                          from Meta by visiting{' '}
                          <Link
                            className="primary-link"
                            to="https://www.facebook.com/help/164968693837950"
                            target="_blank"
                          >
                            this page
                          </Link>
                          .
                        </li>
                        <li className="privacy-policy__list-dashed-item">
                          To opt out from Meta's interest-based ads, follow{' '}
                          <Link
                            className="primary-link"
                            to="https://www.facebook.com/help/568137493302217"
                            target="_blank"
                          >
                            these
                          </Link>{' '}
                          instructions from Meta.
                        </li>
                        <li className="privacy-policy__list-dashed-item">
                          Meta adheres to the Self-Regulatory Principles for
                          Online Behavioural Advertising established by the
                          Digital Advertising Alliance. You can also opt out
                          from Meta and other participating companies through
                          the{' '}
                          <Link
                            className="primary-link"
                            to="https://youradchoices.com/"
                            target="_blank"
                          >
                            Digital Advertising Alliance
                          </Link>{' '}
                          in the USA , the{' '}
                          <Link
                            className="primary-link"
                            to="http://youradchoices.ca/ "
                            target="_blank"
                          >
                            Digital Advertising Alliance of Canada
                          </Link>{' '}
                          in Canada or the{' '}
                          <Link
                            className="primary-link"
                            to="http://www.youronlinechoices.eu/ "
                            target="_blank"
                          >
                            European Interactive Digital Advertising Alliance
                          </Link>{' '}
                          in Europe, or opt out using your mobile device
                          settings. instructions from Meta.
                        </li>
                        <li className="privacy-policy__list-dashed-item">
                          For more information on the privacy practices of Meta,
                          please visit{' '}
                          <Link
                            className="primary-link"
                            to=" https://www.facebook.com/privacy/explanation"
                            target="_blank"
                          >
                            Meta's Data Policy
                          </Link>{' '}
                        </li>
                      </ul>
                    </li>
                  </ul>
                </li>
              </li>
            </ul>
          </li>
          <li className="privacy-policy__numbered-list-item">
            <h2 className="privacy-policy__subtitle">GDPR Privacy</h2>
            <ul className="privacy-policy__list">
              <li className="privacy-policy__list-item">
                <h4>Legal Basis for Processing Personal Data under GDPR</h4>
                <p className="privacy-policy__list-item">
                  {' '}
                  We may process Personal Data under the following conditions:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__list-dashed-item">
                    <strong>Consent:</strong> Consent: You have given Your
                    consent for processing Personal Data for one or more
                    specific purposes.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    <strong>Performance of a contract:</strong> Provision of
                    Personal Data is necessary for the performance of an
                    agreement with You and/or for any pre-contractual
                    obligations thereof.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    <strong>Legal obligations:</strong> Processing Personal Data
                    is necessary for compliance with a legal obligation to which
                    the Company is subject.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    <strong>Vital interests:</strong> Processing Personal Data
                    is necessary in order to protect Your vital interests or of
                    another natural person.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    <strong>Public interests:</strong> Processing Personal Data
                    is related to a task that is carried out in the public
                    interest or in the exercise of official authority vested in
                    the Company.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    <strong>Legitimate interests:</strong> Processing Personal
                    Data is necessary for the purposes of the legitimate
                    interests pursued by the Company.
                  </li>
                  <p className="privacy-policy__list-item">
                    In any case, the Company will gladly help to clarify the
                    specific legal basis that applies to the processing, and in
                    particular whether the provision of Personal Data is a
                    statutory or contractual requirement, or a requirement
                    necessary to enter into a contract.
                  </p>
                </ul>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Your Rights under the GDPR</h4>
                <p className="privacy-policy__list-item">
                  The Company undertakes to respect the confidentiality of Your
                  Personal Data and to guarantee You can exercise Your rights.
                </p>
                <p className="privacy-policy__list-item">
                  You have the right under this Privacy Policy, and by law if
                  You are within the EU, to:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__list-dashed-item">
                    Request access to Your Personal Data. The right to access,
                    update or delete the information We have on You. Whenever
                    made possible, you can access, update or request deletion of
                    Your Personal Data directly within Your account settings
                    section. If you are unable to perform these actions
                    yourself, please contact Us to assist You. This also enables
                    You to receive a copy of the Personal Data We hold about
                    You.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Request correction of the Personal Data that We hold about
                    You. You have the right to to have any incomplete or
                    inaccurate information We hold about You corrected.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Object to processing of Your Personal Data. This right
                    exists where We are relying on a legitimate interest as the
                    legal basis for Our processing and there is something about
                    Your particular situation, which makes You want to object to
                    our processing of Your Personal Data on this ground. You
                    also have the right to object where We are processing Your
                    Personal Data for direct marketing purposes.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Request erasure of Your Personal Data. You have the right to
                    ask Us to delete or remove Personal Data when there is no
                    good reason for Us to continue processing it.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Request the transfer of Your Personal Data. We will provide
                    to You, or to a third-party You have chosen, Your Personal
                    Data in a structured, commonly used, machine-readable
                    format. Please note that this right only applies to
                    automated information which You initially provided consent
                    for Us to use or where We used the information to perform a
                    contract with You.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Withdraw Your consent. You have the right to withdraw Your
                    consent on using your Personal Data. If You withdraw Your
                    consent, We may not be able to provide You with access to
                    certain specific functionalities of the Service.
                  </li>
                </ul>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Exercising of Your GDPR Data Protection Rights</h4>
                <p className="privacy-policy__list-item">
                  You may exercise Your rights of access, rectification,
                  cancellation and opposition by contacting Us. Please note that
                  we may ask You to verify Your identity before responding to
                  such requests. If You make a request, We will try our best to
                  respond to You as soon as possible.
                </p>
                <p className="privacy-policy__list-item">
                  You have the right to complain to a Data Protection Authority
                  about Our collection and use of Your Personal Data. For more
                  information, if You are in the European Economic Area (EEA),
                  please contact Your local data protection authority in the
                  EEA.
                </p>
              </li>
            </ul>
          </li>
          <li className="privacy-policy__numbered-list-item">
            <h2 className="privacy-policy__subtitle">CCPA Privacy</h2>
            <p className="privacy-policy__paragraph">
              This privacy notice section for California residents supplements
              the information contained in Our Privacy Policy and it applies
              solely to all visitors, users, and others who reside in the State
              of California.
            </p>
            <ul className="privacy-policy__list">
              <li className="privacy-policy__list-item">
                <h4>Categories of Personal Information Collected</h4>
                <p className="privacy-policy__list-item">
                  We collect information that identifies, relates to, describes,
                  references, is capable of being associated with, or could
                  reasonably be linked, directly or indirectly, with a
                  particular Consumer or Device. The following is a list of
                  categories of personal information which we may collect or may
                  have been collected from California residents within the last
                  twelve (12) months.
                </p>
                <p className="privacy-policy__list-item">
                  Please note that the categories and examples provided in the
                  list below are those defined in the CCPA. This does not mean
                  that all examples of that category of personal information
                  were in fact collected by Us, but reflects our good faith
                  belief to the best of our knowledge that some of that
                  information from the applicable category may be and may have
                  been collected. For example, certain categories of personal
                  information would only be collected if You provided such
                  personal information directly to Us.
                </p>
                <ul className="privacy-policy__roman-list">
                  <li className="privacy-policy__list-roman-item">
                    <h4>Category A: Identifiers.</h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Examples:</strong> A real name, alias, postal
                        address, unique personal identifier, online identifier,
                        Internet Protocol address, email address, account name,
                        driver's license number, passport number, or other
                        similar identifiers.
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Collected:</strong> Yes.
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>
                      Category B: Personal information categories listed in the
                      California Customer Records statute (Cal. Civ. Code §
                      1798.80(e)).
                    </h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Examples:</strong> A name, signature, Social
                        Security number, physical characteristics or
                        description, address, telephone number, passport number,
                        driver's license or state identification card number,
                        insurance policy number, education, employment,
                        employment history, bank account number, credit card
                        number, debit card number, or any other financial
                        information, medical information, or health insurance
                        information. Some personal information included in this
                        category may overlap with other categories.
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Collected:</strong> Yes.
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>
                      Category C: Protected classification characteristics under
                      California or federal law.
                    </h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Examples:</strong> (40 years or older), race,
                        color, ancestry, national origin, citizenship, religion
                        or creed, marital status, medical condition, physical or
                        mental disability, sex (including gender, gender
                        identity, gender expression, pregnancy or childbirth and
                        related medical conditions), sexual orientation, veteran
                        or military status, genetic information (including
                        familial genetic information).
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Collected:</strong> No.
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>Category D: Commercial information.</h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Examples:</strong> Records and history of
                        products or services purchased or considered.
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Collected:</strong> Yes.
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>Category E: Biometric information.</h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Examples:</strong> Genetic, physiological,
                        behavioral, and biological characteristics, or activity
                        patterns used to extract a template or other identifier
                        or identifying information, such as, fingerprints,
                        faceprints, and voiceprints, iris or retina scans,
                        keystroke, gait, or other physical patterns, and sleep,
                        health, or exercise data.
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Collected:</strong> No.
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>
                      Category F: Internet or other similar network activity.
                    </h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Examples:</strong> Interaction with our Service
                        or advertisement.
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Collected:</strong> Yes.
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>Category G: Geolocation data.</h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Examples:</strong> Approximate physical
                        location.
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Collected:</strong> No.
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>Category H: Sensory data.</h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Examples:</strong> Audio, electronic, visual,
                        thermal, olfactory, or similar information.
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Collected:</strong> No.
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>
                      Category I: Professional or employment-related
                      information.
                    </h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Examples:</strong> Current or past job history
                        or performance evaluations.
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Collected:</strong> No.
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>
                      Category J: Non-public education information (per the
                      Family Educational Rights and Privacy Act (20 U.S.C.
                      Section 1232g, 34 C.F.R. Part 99)).
                    </h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Examples:</strong> Education records directly
                        related to a student maintained by an educational
                        institution or party acting on its behalf, such as
                        grades, transcripts, class lists, student schedules,
                        student identification codes, student financial
                        information, or student disciplinary records.
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Collected:</strong> No.
                      </li>
                    </ul>
                  </li>
                  <li className="privacy-policy__list-roman-item">
                    <h4>
                      Category K: Inferences drawn from other personal
                      information.
                    </h4>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Examples:</strong> Profile reflecting a person's
                        preferences, characteristics, psychological trends,
                        predispositions, behavior, attitudes, intelligence,
                        abilities, and aptitudes.
                      </li>
                      <li className="privacy-policy__list-dashed-item">
                        <strong>Collected:</strong> No.
                      </li>
                    </ul>
                  </li>
                </ul>
                <p className="privacy-policy__list-item">
                  Under CCPA, personal information does not include:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__list-dashed-item">
                    Publicly available information from government records
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Deidentified or aggregated consumer information
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Information excluded from the CCPA's scope.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Health or medical information covered by the Health
                    Insurance Portability and Accountability Act of 1996 (HIPAA)
                    and the California Confidentiality of Medical Information
                    Act (CMIA) or clinical trial data.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Personal Information covered by certain sector-specific
                    privacy laws, including the Fair Credit Reporting Act
                    (FRCA), the Gramm-Leach-Bliley Act (GLBA) or California
                    Financial Information Privacy Act (FIPA), and the Driver's
                    Privacy Protection Act of 1994.
                  </li>
                </ul>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Sources of Personal Information</h4>
                <p className="privacy-policy__list-item">
                  We obtain the categories of personal information listed above
                  from the following categories of sources:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__list-dashed-item">
                    Directly from You. For example, from the forms You complete
                    on our Service, preferences You express or provide through
                    our Service, or from Your purchases on our Service.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Indirectly from You. For example, from observing Your
                    activity on our Service.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    Automatically from You. For example, through cookies We or
                    our Service Providers set on Your Device as You navigate
                    through our Service.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    From Service Providers. For example, third-party vendors to
                    monitor and analyze the use of our Service, third-party
                    vendors to provide advertising on our Service, third-party
                    vendors to deliver targeted advertising to You, third-party
                    vendors for payment processing, or other third-party vendors
                    that We use to provide the Service to You.
                  </li>
                </ul>
              </li>
              <li className="privacy-policy__list-item">
                <h4>
                  Use of Personal Information for Business Purposes or
                  Commercial Purposes
                </h4>
                <p className="privacy-policy__list-item">
                  We may use or disclose personal information We collect for
                  "business purposes" or "commercial purposes" (as defined under
                  the CCPA), which may include the following examples:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__list-dashed-item">
                    To operate our Service and provide You with our Service.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    To provide You with support and to respond to Your
                    inquiries, including to investigate and address Your
                    concerns and monitor and improve our Service.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    To fulfill or meet the reason You provided the information.
                    For example, if You share Your contact information to ask a
                    question about our Service, We will use that personal
                    information to respond to Your inquiry. If You provide Your
                    personal information to purchase a product or service, We
                    will use that information to process Your payment and
                    facilitate delivery.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    To respond to law enforcement requests and as required by
                    applicable law, court order, or governmental regulations.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    As described to You when collecting Your personal
                    information or as otherwise set forth in the CCPA.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    For internal administrative and auditing purposes.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    To detect security incidents and protect against malicious,
                    deceptive, fraudulent or illegal activity, including, when
                    necessary, to prosecute those responsible for such
                    activities.
                  </li>
                </ul>
                <p className="privacy-policy__list-item">
                  Please note that the examples provided above are illustrative
                  and not intended to be exhaustive. For more details on how we
                  use this information, please refer to the "Use of Your
                  Personal Data" section.
                </p>
                <p className="privacy-policy__list-item">
                  If We decide to collect additional categories of personal
                  information or use the personal information We collected for
                  materially different, unrelated, or incompatible purposes We
                  will update this Privacy Policy.
                </p>
              </li>
              <li className="privacy-policy__list-item">
                <h4>
                  Disclosure of Personal Information for Business Purposes or
                  Commercial Purposes
                </h4>
                <p className="privacy-policy__list-item">
                  We may use or disclose and may have used or disclosed in the
                  last twelve (12) months the following categories of personal
                  information for business or commercial purposes:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__list-dashed-item">
                    <strong>Category A:</strong> Identifiers
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    <strong>Category B:</strong> Personal information categories
                    listed in the California Customer Records statute (Cal. Civ.
                    Code § 1798.80(e))
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    <strong>Category D:</strong> Commercial information
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    <strong>Category F:</strong> Internet or other similar
                    network activity
                  </li>
                </ul>
                <p className="privacy-policy__list-item">
                  Please note that the categories listed above are those defined
                  in the CCPA. This does not mean that all examples of that
                  category of personal information were in fact disclosed, but
                  reflects our good faith belief to the best of our knowledge
                  that some of that information from the applicable category may
                  be and may have been disclosed.
                </p>
                <p className="privacy-policy__list-item">
                  When We disclose personal information for a business purpose
                  or a commercial purpose, We enter a contract that describes
                  the purpose and requires the recipient to both keep that
                  personal information confidential and not use it for any
                  purpose except performing the contract.
                </p>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Sale of Personal Information</h4>
                <p className="privacy-policy__list-item">
                  As defined in the CCPA, "sell" and "sale" mean selling,
                  renting, releasing, disclosing, disseminating, making
                  available, transferring, or otherwise communicating orally, in
                  writing, or by electronic or other means, a consumer's
                  personal information by the business to a third party for
                  valuable consideration. This means that We may have received
                  some kind of benefit in return for sharing personal
                  information, but not necessarily a monetary benefit.
                </p>
                <p className="privacy-policy__list-item">
                  Please note that the categories listed below are those defined
                  in the CCPA. This does not mean that all examples of that
                  category of personal information were in fact sold, but
                  reflects our good faith belief to the best of our knowledge
                  that some of that information from the applicable category may
                  be and may have been shared for value in return.
                </p>
                <p className="privacy-policy__list-item">
                  We may sell and may have sold in the last twelve (12) months
                  the following categories of personal information:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__list-dashed-item">
                    <strong>Category A:</strong> Identifiers
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    <strong>Category B:</strong> Personal information categories
                    listed in the California Customer Records statute (Cal. Civ.
                    Code § 1798.80(e))
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    <strong>Category D:</strong> Commercial information
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    <strong>Category F:</strong> Internet or other similar
                    network activity
                  </li>
                </ul>
              </li>
            </ul>
          </li>
          <li className="privacy-policy__numbered-list-item">
            <h2 className="privacy-policy__subtitle">
              Share of Personal Information
            </h2>
            <p className="privacy-policy__paragraph">
              We may share Your personal information identified in the above
              categories with the following categories of third parties:
            </p>
            <ul className="privacy-policy__dashed-list ml-1rem">
              <li className="privacy-policy__list-dashed-item">
                Service Providers
              </li>
              <li className="privacy-policy__list-dashed-item">
                Payment processors
              </li>
              <li className="privacy-policy__list-dashed-item">
                Our affiliates
              </li>
              <li className="privacy-policy__list-dashed-item">
                Our business partners
              </li>
              <li className="privacy-policy__list-dashed-item">
                Third party vendors to whom You or Your agents authorize Us to
                disclose Your personal information in connection with products
                or services We provide to You
              </li>
            </ul>
            <ul className="privacy-policy__list">
              <li className="privacy-policy__list-item">
                <h4>
                  Sale of Personal Information of Minors Under 16 Years of Age
                </h4>
                <p className="privacy-policy__list-item">
                  We do not sell the personal information of Consumers We
                  actually know are less than 16 years of age, unless We receive
                  affirmative authorization (the "right to opt-in") from either
                  the Consumer who is between 13 and 16 years of age, or the
                  parent or guardian of a Consumer less than 13 years of age.
                  Consumers who opt-in to the sale of personal information may
                  opt-out of future sales at any time. To exercise the right to
                  opt-out, You (or Your authorized representative) may submit a
                  request to Us by contacting Us.
                </p>
                <p className="privacy-policy__list-item">
                  If You have reason to believe that a child under the age of 13
                  (or 16) has provided Us with personal information, please
                  contact Us with sufficient detail to enable Us to delete that
                  information.
                </p>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Your Rights under the CCPA</h4>
                <p className="privacy-policy__list-item">
                  The CCPA provides California residents with specific rights
                  regarding their personal information. If You are a resident of
                  California, You have the following rights:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__list-dashed-item">
                    The right to notice. You have the right to be notified which
                    categories of Personal Data are being collected and the
                    purposes for which the Personal Data is being used.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    The right to request. Under CCPA, You have the right to
                    request that We disclose information to You about Our
                    collection, use, sale, disclosure for business purposes and
                    share of personal information. Once We receive and confirm
                    Your request, We will disclose to You:
                  </li>
                  <ul className="privacy-policy__disc-list">
                    <li className="privacy-policy__disc-list-item">
                      The categories of personal information We collected about
                      You
                    </li>
                    <li className="privacy-policy__disc-list-item">
                      The categories of sources for the personal information We
                      collected about You
                    </li>
                    <li className="privacy-policy__disc-list-item">
                      Our business or commercial purpose for collecting or
                      selling that personal information
                    </li>
                    <li className="privacy-policy__disc-list-item">
                      The categories of third parties with whom We share that
                      personal information
                    </li>
                    <li className="privacy-policy__disc-list-item">
                      The specific pieces of personal information We collected
                      about You
                    </li>
                    <li className="privacy-policy__disc-list-item">
                      The specific pieces of personal information We collected
                      about You
                    </li>
                    <ul className="privacy-policy__circle-list">
                      <li className="privacy-policy__circle-list-item">
                        The categories of personal information categories sold
                      </li>
                      <li className="privacy-policy__circle-list-item">
                        The categories of personal information categories
                        disclosed
                      </li>
                    </ul>
                  </ul>
                  <li className="privacy-policy__list-dashed-item">
                    The right to say no to the sale of Personal Data (opt-out).
                    You have the right to direct Us to not sell Your personal
                    information. To submit an opt-out request please contact Us.
                  </li>
                  <li className="privacy-policy__list-dashed-item">
                    The right to delete Personal Data. You have the right to
                    request the deletion of Your Personal Data, subject to
                    certain exceptions. Once We receive and confirm Your
                    request, We will delete (and direct Our Service Providers to
                    delete) Your personal information from our records, unless
                    an exception applies. We may deny Your deletion request if
                    retaining the information is necessary for Us or Our Service
                    Providers to:
                  </li>
                  <ul className="privacy-policy__disc-list">
                    <li className="privacy-policy__disc-list-item">
                      Complete the transaction for which We collected the
                      personal information, provide a good or service that You
                      requested, take actions reasonably anticipated within the
                      context of our ongoing business relationship with You, or
                      otherwise perform our contract with You.
                    </li>
                    <li className="privacy-policy__disc-list-item">
                      Detect security incidents, protect against malicious,
                      deceptive, fraudulent, or illegal activity, or prosecute
                      those responsible for such activities.
                    </li>
                    <li className="privacy-policy__disc-list-item">
                      Debug products to identify and repair errors that impair
                      existing intended functionality.
                    </li>
                    <li className="privacy-policy__disc-list-item">
                      Exercise free speech, ensure the right of another consumer
                      to exercise their free speech rights, or exercise another
                      right provided for by law.
                    </li>
                    <li className="privacy-policy__disc-list-item">
                      Comply with the California Electronic Communications
                      Privacy Act (Cal. Penal Code § 1546 et. seq.).
                    </li>
                    <li className="privacy-policy__disc-list-item">
                      Engage in public or peer-reviewed scientific, historical,
                      or statistical research in the public interest that
                      adheres to all other applicable ethics and privacy laws,
                      when the information's deletion may likely render
                      impossible or seriously impair the research's achievement,
                      if You previously provided informed consent.
                    </li>
                    <li className="privacy-policy__disc-list-item">
                      Enable solely internal uses that are reasonably aligned
                      with consumer expectations based on Your relationship with
                      Us.
                    </li>
                    <li className="privacy-policy__disc-list-item">
                      Comply with a legal obligation.
                    </li>
                    <li className="privacy-policy__disc-list-item">
                      Make other internal and lawful uses of that information
                      that are compatible with the context in which You provided
                      it.
                    </li>
                  </ul>
                  <li className="privacy-policy__list-dashed-item">
                    The right not to be discriminated against. You have the
                    right not to be discriminated against for exercising any of
                    Your consumer's rights, including by:
                    <ul className="privacy-policy__disc-list">
                      <li className="privacy-policy__disc-list-item">
                        Denying goods or services to You
                      </li>
                      <li className="privacy-policy__disc-list-item">
                        Charging different prices or rates for goods or
                        services, including the use of discounts or other
                        benefits or imposing penalties
                      </li>
                      <li className="privacy-policy__disc-list-item">
                        Providing a different level or quality of goods or
                        services to You
                      </li>
                      <li className="privacy-policy__disc-list-item">
                        Suggesting that You will receive a different price or
                        rate for goods or services or a different level or
                        quality of goods or services
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Exercising Your CCPA Data Protection Rights</h4>
                <p className="privacy-policy__list-item">
                  In order to exercise any of Your rights under the CCPA, and if
                  You are a California resident, You can contact Us:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__dashed-list-item">
                    By email: Bredrin@Astreaux.ai
                  </li>
                </ul>
                <p className="privacy-policy__list-item">
                  Only You, or a person registered with the California Secretary
                  of State that You authorize to act on Your behalf, may make a
                  verifiable request related to Your personal information.
                </p>
                <p className="privacy-policy__list-item">
                  Your request to Us must:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__dashed-list-item">
                    Provide sufficient information that allows Us to reasonably
                    verify You are the person about whom We collected personal
                    information or an authorized representative
                  </li>
                  <li className="privacy-policy__dashed-list-item">
                    Describe Your request with sufficient detail that allows Us
                    to properly understand, evaluate, and respond to it
                  </li>
                </ul>
                <p className="privacy-policy__list-item">
                  We cannot respond to Your request or provide You with the
                  required information if we cannot:
                </p>
                <ul className="privacy-policy__dashed-list">
                  <li className="privacy-policy__dashed-list-item">
                    Verify Your identity or authority to make the request
                  </li>
                  <li className="privacy-policy__dashed-list-item">
                    And confirm that the personal information relates to You
                  </li>
                </ul>
                <p className="privacy-policy__list-item">
                  We will disclose and deliver the required information free of
                  charge within 45 days of receiving Your verifiable request.
                  The time period to provide the required information may be
                  extended once by an additional 45 days when reasonable
                  necessary and with prior notice.
                </p>
                <p className="privacy-policy__list-item">
                  Any disclosures We provide will only cover the 12-month period
                  preceding the verifiable request's receipt.
                </p>
                <p className="privacy-policy__list-item">
                  For data portability requests, We will select a format to
                  provide Your personal information that is readily useable and
                  should allow You to transmit the information from one entity
                  to another entity without hindrance.
                </p>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Do Not Sell My Personal Information</h4>
                <p className="privacy-policy__list-item">
                  You have the right to opt-out of the sale of Your personal
                  information. Once We receive and confirm a verifiable consumer
                  request from You, we will stop selling Your personal
                  information. To exercise Your right to opt-out, please contact
                  Us.
                </p>
                <p className="privacy-policy__list-item">
                  The Service Providers we partner with (for example, our
                  analytics or advertising partners) may use technology on the
                  Service that sells personal information as defined by the CCPA
                  law. If you wish to opt out of the use of Your personal
                  information for interest-based advertising purposes and these
                  potential sales as defined under CCPA law, you may do so by
                  following the instructions below.
                </p>
                <p className="privacy-policy__list-item">
                  Please note that any opt out is specific to the browser You
                  use. You may need to opt out on every browser that You use.
                </p>
                <ul className="privacy-policy__roman-list">
                  <li className="privacy-policy__roman-list-item">
                    <h4>Website</h4>
                    <p className="privacy-policy__list-item">
                      You can opt out of receiving ads that are personalized as
                      served by our Service Providers by following our
                      instructions presented on the Service:
                    </p>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__dashed-list-item">
                        <Link
                          to="http://www.networkadvertising.org/choices/"
                          target="_blank"
                          className="primary-link"
                        >
                          The NAI's opt-out platform
                        </Link>
                      </li>
                      <li className="privacy-policy__dashed-list-item">
                        <Link
                          to="http://www.youronlinechoices.com/"
                          target="_blank"
                          className="primary-link"
                        >
                          The EDAA's opt-out platform
                        </Link>
                      </li>
                      <li className="privacy-policy__dashed-list-item">
                        <Link
                          to="http://optout.aboutads.info/?c=2⟨=EN"
                          target="_blank"
                          className="primary-link"
                        >
                          The DAA's opt-out platform:
                        </Link>
                      </li>
                    </ul>
                    <p className="privacy-policy__list-item">
                      The opt out will place a cookie on Your computer that is
                      unique to the browser You use to opt out. If you change
                      browsers or delete the cookies saved by your browser, You
                      will need to opt out again.
                    </p>
                  </li>
                  <li className="privacy-policy__roman-list-item">
                    <h4>Mobile Devices</h4>
                    <p className="privacy-policy__list-item">
                      Your mobile device may give You the ability to opt out of
                      the use of information about the apps You use in order to
                      serve You ads that are targeted to Your interests:
                    </p>
                    <ul className="privacy-policy__dashed-list">
                      <li className="privacy-policy__list-item">
                        "Opt out of Interest-Based Ads" or "Opt out of Ads
                        Personalization" on Android devices
                      </li>
                      <li className="privacy-policy__list-item">
                        "Limit Ad Tracking" on iOS devices
                      </li>
                    </ul>
                    <p className="privacy-policy__list-item">
                      You can also stop the collection of location information
                      from Your mobile device by changing the preferences on
                      Your mobile device.
                    </p>
                  </li>
                </ul>
              </li>
              <li className="privacy-policy__list-item">
                <h4>
                  "Do Not Track" Policy as Required by California Online Privacy
                  Protection Act (CalOPPA) Our Service does not respond to Do
                  Not Track signals.
                </h4>
                <p className="privacy-policy__list-item">
                  However, some third-party websites do keep track of Your
                  browsing activities. If You are visiting such websites, You
                  can set Your preferences in Your web browser to inform
                  websites that You do not want to be tracked. You can enable or
                  disable DNT by visiting the preferences or settings page of
                  Your web browser.
                </p>
              </li>
              <li className="privacy-policy__list-item">
                <h4>
                  Your California Privacy Rights (California's Shine the Light
                  law)
                </h4>
                <p className="privacy-policy__list-item">
                  Under California Civil Code Section 1798 (California's Shine
                  the Light law), California residents with an established
                  business relationship with us can request information once a
                  year about sharing their Personal Data with third parties for
                  the third parties' direct marketing purposes.
                </p>
                <p className="privacy-policy__list-item">
                  If you'd like to request more information under the California
                  Shine the Light law, and if You are a California resident, You
                  can contact Us using the contact information provided below.
                </p>
              </li>
              <li className="privacy-policy__list-item">
                <h4>
                  California Privacy Rights for Minor Users (California Business
                  and Professions Code Section 22581)
                </h4>
                <p className="privacy-policy__list-item">
                  California Business and Professions Code section 22581 allow
                  California residents under the age of 18 who are registered
                  users of online sites, services or applications to request and
                  obtain removal of content or information they have publicly
                  posted.
                </p>
                <p className="privacy-policy__list-item">
                  To request removal of such data, and if You are a California
                  resident, You can contact Us using the contact information
                  provided below, and include the email address associated with
                  Your account.
                </p>
                <p className="privacy-policy__list-item">
                  Be aware that Your request does not guarantee complete or
                  comprehensive removal of content or information posted online
                  and that the law may not permit or require removal in certain
                  circumstances.
                </p>
              </li>
            </ul>
          </li>
          <li className="privacy-policy__numbered-list-item">
            <h2 className="privacy-policy__subtitle">Miscellaneous</h2>
            <ul className="privacy-policy__list">
              <li className="privacy-policy__list-item">
                <h4>Links to Other Websites</h4>
                <p className="privacy-policy__list-item">
                  Our Service may contain links to other websites that are not
                  operated by Us. If You click on a third-party link, You will
                  be directed to that third party's site. We strongly advise You
                  to review the Privacy Policy of every site You visit.
                </p>
                <p className="privacy-policy__list-item">
                  We have no control over and assume no responsibility for the
                  content, privacy policies or practices of any third-party
                  sites or services.
                </p>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Changes to this Privacy Policy</h4>
                <p className="privacy-policy__list-item">
                  We may update Our Privacy Policy from time to time. We will
                  notify You of any changes by posting the new Privacy Policy on
                  this page.
                </p>
                <p className="privacy-policy__list-item">
                  We will let You know via email and/or a prominent notice on
                  Our Service, prior to the change becoming effective and update
                  the "Last updated" date at the top of this Privacy Policy.
                </p>
                <p className="privacy-policy__list-item">
                  You are advised to review this Privacy Policy periodically for
                  any changes. Changes to this Privacy Policy are effective when
                  they are posted on this page.
                </p>
              </li>
              <li className="privacy-policy__list-item">
                <h4>Contact Information</h4>
                <p className="privacy-policy__list-item">
                  If you have any questions about this Privacy Policy, You can
                  contact us by email: Bredrin@Astreaux.ai
                </p>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
